import { getFunctions, httpsCallable } from 'firebase/functions';

type AiTask = 'fix_address' | 'categorize_item' | 'polish_text' | 'project_summary';

interface AiAssistResponse<T> {
  result: T;
  usage: { inputTokens: number; outputTokens: number };
}

interface AddressFixResult {
  street: string;
  accessCode: string;
  accessInfo: string;
  postalCode: string;
  city: string;
  /** Contact details the model found parked in an address field. */
  contactName?: string;
  contactPhone?: string;
  contactEmail?: string;
  changes: string[];
  confidence: number;
}

interface CategorizeResult {
  category: string;
  subcategory?: string;
  confidence: number;
}

interface PolishTextResult {
  text: string;
  changes: string[];
  hadErrors: boolean;
}

interface ProjectSummaryResult {
  summary: string;
  description: string;
}

const functions = getFunctions();

async function callAiAssist<T>(task: AiTask, input: Record<string, any>): Promise<T> {
  const aiAssist = httpsCallable(functions, 'aiAssist');
  const response = await aiAssist({ task, input });
  const data = response.data as AiAssistResponse<T> | undefined;
  if (!data || !data.result) {
    throw new Error(`AI assist returned unexpected response for task: ${task}`);
  }
  return data.result;
}

export async function fixAddress(
  street: string, postalCode: string, city: string,
  accessCode?: string, accessInfo?: string,
  contact?: { name?: string; phone?: string; email?: string }
): Promise<AddressFixResult> {
  return callAiAssist<AddressFixResult>('fix_address', {
    street, postalCode, city, accessCode, accessInfo,
    contactName: contact?.name, contactPhone: contact?.phone, contactEmail: contact?.email,
  });
}

export async function categorizeItem(name: string, description: string): Promise<CategorizeResult> {
  return callAiAssist<CategorizeResult>('categorize_item', { name, description });
}

export async function polishText(text: string, fieldType?: string): Promise<PolishTextResult> {
  return callAiAssist<PolishTextResult>('polish_text', { text, fieldType });
}

export async function generateProjectSummary(input: {
  street: string;
  city: string;
  contactName: string;
  contactPhone: string;
  eventType: string;
  notes: string;
}): Promise<ProjectSummaryResult> {
  return callAiAssist<ProjectSummaryResult>('project_summary', input);
}
